import React, { useState } from 'react';
import type { Fee, FeeType } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { CostIcon } from '@hero/web-icons';
import { type OrderDisplayPriceUi } from '@hero/hooks-shared';
import { Price } from '../../../../app/Price';
import { Collapse } from '@hero/web-components';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface OrderPriceDetailsProps {
  priceUi: OrderDisplayPriceUi;
}

/**
 * Auto-managed fees we hide from the collapsed-header sum — kept in
 * sync with `PriceCell` and `OrderSummaryCard` so all three surfaces
 * agree on what counts as a user-visible fee.
 */
const HIDDEN_FEE_TYPES = new Set(['items_insurance']);

export const OrderPriceDetails: React.FC<OrderPriceDetailsProps> = ({ priceUi }) => {
  const { t } = useTranslation(['c-price', 'm-order']);
  const [showMore, setShowMore] = useState(false);
  const [showFees, setShowFees] = useState(false);

  if (!priceUi.showSection) {
    return null;
  }

  const amount = priceUi.amount;
  const hasPrice = priceUi.hasPrice;

  if (!amount || !hasPrice) {
    return (
      <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-start gap-3">
          <div className="p-1.5 rounded-lg bg-gray-50">
            <CostIcon className="size-5 text-gray-400" />
          </div>
          <div className="flex-1">
            <p className="text-gray-700 text-[14px] font-[500] leading-[22px]">
              {t('not_priced')}
            </p>
            <p className="text-gray-500 text-[13px] leading-[20px] mt-0.5">
              {t('not_priced_hint')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currency = amount.currency;

  const renderPrice = (value: number) => <Price amount={value} currency={currency} />;

  const feesApplied = amount.fees.applied;
  const hasFees = feesApplied.length > 0;

  const getFeeLabel = (type: FeeType) => t(`c-price:fee_types.${type}`);

  const visibleFees: Fee[] = feesApplied.filter(f => !HIDDEN_FEE_TYPES.has(f.type));
  const visibleFeesTotal = visibleFees.reduce((acc, f) => acc + (f.amount || 0), 0);

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={
          <div className="flex flex-col flex-1 me-3 gap-0.5">
            <span className="text-gray-900 text-[18px] font-[700] leading-[28px]">
              {renderPrice(amount.itemsOriginalSubtotal)}
            </span>
            {visibleFeesTotal > 0 && (
              <span className="flex items-center gap-1 text-[12px] leading-[16px] text-gray-500">
                <span>{t('m-order:table.price_cell.fees')}:</span>
                <Price amount={visibleFeesTotal} currency={currency} size="xs" />
              </span>
            )}
          </div>
        }
        icon={
          <div className="p-1.5 bg-green-50 rounded-lg">
            <CostIcon className="size-5 text-green-600" />
          </div>
        }
        defaultOpen={false}>
        <div className="space-y-3">
          {/* Fees */}
          <div>
            <button
              type="button"
              onClick={() => hasFees && setShowFees(!showFees)}
              className={`flex items-center justify-between w-full  ${hasFees ? 'cursor-pointer' : 'cursor-default'}`}>
              <span className="flex items-center gap-1 text-gray-600 text-[14px] leading-[22px] ">
                {t('fees')}
                {hasFees &&
                  (showFees ? (
                    <ChevronUp className="size-3.5 text-gray-400" />
                  ) : (
                    <ChevronDown className="size-3.5 text-gray-400" />
                  ))}
              </span>
              <span className="text-gray-600 text-[14px] font-[500] leading-[22px]">
                {renderPrice(amount.fees.total)}
              </span>
            </button>

            <div
              className={`overflow-hidden transition-all duration-150 ease-out ${
                showFees ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              {showFees && (
                <div className="mt-2 space-y-2">
                  {feesApplied.map((fee, index) => (
                    <div
                      key={`${fee.type}-${index}`}
                      className="flex items-center justify-between  ps-3">
                      <span className="text-gray-500 text-[13px] leading-[20px]">
                        {getFeeLabel(fee.type)}
                      </span>
                      <span className="text-gray-700 text-[13px] font-[500] leading-[20px]">
                        {renderPrice(fee.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tax */}
          <div className="flex items-center justify-between ">
            <span className="text-gray-600 text-[14px] leading-[22px]">
              {t('tax')}
              {amount.tax.applied?.[0]?.percentage
                ? ` (${Math.round(amount.tax.applied[0].percentage * 100)}%)`
                : ''}
            </span>
            <span className="text-gray-600 text-[14px] font-[500] leading-[22px]">
              {renderPrice(amount.tax.total)}
            </span>
          </div>

          {/* Show More Section */}
          <div
            className={`overflow-hidden transition-all duration-150 ease-out ${
              showMore ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
            {showMore && (
              <div className="space-y-3">
                <div className="h-px bg-gray-200 my-2" />

                <div className="flex items-center justify-between ">
                  <span className="text-gray-600 text-[14px] leading-[22px]">
                    {t('paid')}
                  </span>
                  <span className="text-gray-600 text-[14px] font-[500] leading-[22px]">
                    {renderPrice(amount.paid)}
                  </span>
                </div>

                <div className="flex items-center justify-between ">
                  <span className="text-gray-600 text-[14px] leading-[22px]">
                    {t('remaining')}
                  </span>
                  <span className="text-gray-600 text-[14px] font-[500] leading-[22px]">
                    {renderPrice(amount.remaining)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-gray-300 my-3" />

          <div className="flex items-center justify-between ">
            <span className="text-gray-900 text-[16px] font-[600] leading-[24px]">
              {t('total')}
            </span>
            <span className="text-gray-900 text-[18px] font-[700] leading-[28px]">
              {renderPrice(amount.total)}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="w-full mt-2 text-primary text-[14px] font-[500] leading-[22px] hover:underline">
            {showMore ? t('show_less') : t('show_more')}
          </button>
        </div>
      </Collapse>
    </div>
  );
};
